/**
 * Question Card Component
 * Displays the current problem/question
 */

import { QUESTION_FONT_MAP } from '@config/constants'
import type { FontSize } from '../../../types'
import { useRef } from 'react'
import { useDynamicFontSize } from '@hooks/useDynamicFontSize'
import { useResponsiveFontPreset } from '@hooks/useResponsiveFontPreset'
import type { ReactNode } from 'react'

interface QuestionCardProps {
  question: string | ReactNode
  fontSize: FontSize
  contentKey?: string | number
  isSpellingMode?: boolean
  wordToSpeak?: string
  onSpeakWord?: () => void
  isSpeaking?: boolean
}

export default function QuestionCard({
  question,
  fontSize,
  contentKey,
  isSpellingMode = false,
  wordToSpeak,
  onSpeakWord,
  isSpeaking = false,
}: QuestionCardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLDivElement | null>(null)
  const responsiveMaxFont = useResponsiveFontPreset({
    desiredSize: fontSize,
    sizeMap: QUESTION_FONT_MAP,
    containerRef,
  })
  const fontSizeStyle = isSpellingMode ? QUESTION_FONT_MAP.small : responsiveMaxFont
  const fontContentKey = contentKey ?? (typeof question === 'string' ? question : 'letters')
  const dynamicFontSize = useDynamicFontSize({
    targetRef: textRef,
    containerRef,
    contentKey: fontContentKey,
    maxFontSize: fontSizeStyle,
    minFontSize: QUESTION_FONT_MAP.small,
  })

  const handleSpeak = () => {
    if (!wordToSpeak || !onSpeakWord) return
    onSpeakWord()
  }

  return (
    <div className="flashcard__question" ref={containerRef}>
      <div
        className={`flashcard__question-text ${isSpellingMode ? 'flashcard__question-text--clickable' : ''}`}
        style={{ fontSize: dynamicFontSize }}
        aria-live="polite"
        aria-busy={isSpeaking}
        onClick={wordToSpeak ? handleSpeak : undefined}
        role={wordToSpeak ? 'button' : undefined}
        tabIndex={wordToSpeak ? 0 : undefined}
        onKeyDown={
          wordToSpeak
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSpeak()
                }
              }
            : undefined
        }
        ref={textRef}
      >
        {question}
      </div>
    </div>
  )
}
